// Base API client for tenant-scoped requests.
// Created inside TenantShell, provided via React context.
// Reads token from OIDC context on every request (always fresh).

import { createContext, useContext } from 'react'

/** Structured error from the backend API. */
export interface ApiError {
  code: string
  message: string
  timestamp: string
}

/** Error thrown by the API client on non-OK responses. */
export class ApiRequestError extends Error {
  readonly status: number
  readonly apiError: ApiError | null
  readonly body: unknown

  constructor(status: number, apiError: ApiError | null, message: string, body?: unknown) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
    this.apiError = apiError
    this.body = body ?? null
  }
}

/** Options for API requests beyond the standard RequestInit. */
export interface ApiRequestOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>
}

/** The API client interface provided via context. */
export interface ApiClient {
  /** Make a GET request to a tenant-scoped API path. */
  get: <T>(path: string) => Promise<T>
  /** Make a POST request with a JSON body. */
  post: <T>(path: string, body?: unknown) => Promise<T>
  /** Make a PUT request with a JSON body. */
  put: <T>(path: string, body?: unknown) => Promise<T>
  /** Make a DELETE request. */
  del: (path: string) => Promise<void>
  /** Raw fetch for cases that don't fit the convenience methods. */
  fetch: (path: string, options?: ApiRequestOptions) => Promise<Response>
}

/**
 * Create an API client bound to a tenant ID (UUID).
 * The getToken function is called on every request to get a fresh OIDC token.
 * The onUnauthorized callback is called on 401 responses to trigger re-auth.
 */
export function createApiClient(tenantId: string, getToken: () => string | undefined): ApiClient {
  const baseUrl = `/api/v1/tenants/${encodeURIComponent(tenantId)}`

  async function request(path: string, options?: ApiRequestOptions): Promise<Response> {
    const token = getToken()
    const headers: Record<string, string> = {
      ...options?.headers,
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Only set Content-Type for requests that have a non-FormData body.
    // FormData bodies need the browser to auto-set multipart/form-data with boundary.
    if (options?.body && !(options.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers,
    })

    if (response.status === 401) {
      // Throw the error — let the component handle it.
      // Don't trigger redirect here to avoid loops during token refresh.
      throw new ApiRequestError(401, null, 'Authentication expired')
    }

    if (!response.ok) {
      let apiError: ApiError | null = null
      let rawBody: Record<string, unknown> | null = null
      try {
        rawBody = (await response.json()) as Record<string, unknown>
        if (rawBody?.error && typeof rawBody.error === 'object') {
          apiError = rawBody.error as ApiError
        }
      } catch {
        // Response body wasn't JSON — use status text
      }

      throw new ApiRequestError(
        response.status,
        apiError,
        apiError?.message ?? (typeof rawBody?.error === 'string' ? rawBody.error : `API request failed: ${response.status}`),
        rawBody,
      )
    }

    return response
  }

  return {
    get: async <T>(path: string): Promise<T> => {
      const response = await request(path)
      return response.json() as Promise<T>
    },

    post: async <T>(path: string, body?: unknown): Promise<T> => {
      const response = await request(path, {
        method: 'POST',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      })
      // Handle 204 No Content
      if (response.status === 204) {
        return undefined as T
      }
      return response.json() as Promise<T>
    },

    put: async <T>(path: string, body?: unknown): Promise<T> => {
      const response = await request(path, {
        method: 'PUT',
        body: body !== undefined ? JSON.stringify(body) : undefined,
      })
      if (response.status === 204) {
        return undefined as T
      }
      return response.json() as Promise<T>
    },

    del: async (path: string): Promise<void> => {
      await request(path, { method: 'DELETE' })
    },

    fetch: request,
  }
}

// React context for the API client
export const ApiClientContext = createContext<ApiClient | null>(null)

/** Hook to access the tenant-scoped API client. Must be used within TenantShell. */
export function useApi(): ApiClient {
  const client = useContext(ApiClientContext)
  if (!client) {
    throw new Error('useApi must be used within an ApiClientProvider (TenantShell)')
  }
  return client
}
